import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import moment from "moment";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import { formatDate } from "../../../helpers/date";

import ReportService from "../../../services/reportService";
import Loading from "../../common/Loading";
import TimeSummaryItem from "./TimeSummaryItem";
import TimeSummaryFilters from "./TimeSummaryFilters";
import { downloadTimeSummaryCSV } from "../utils";
import { TIME_SUMMARY_COLUMNS, SUMMARY_COLUMNS } from "./columns";
import * as exportActions from "../../../actions/exportActions";
import "./TimeSummary.scss";

const TimeSummaryPageList = ({ data, loading }) => {
  if (loading)
    return (
      <div className="loading">
        <Loading />
      </div>
    );
  return data && data.length ? (
    <React.Fragment>
      {data.map(({ _id: id, customer, calls, callTypes }) => (
        <TimeSummaryItem
          key={id}
          customer={customer.name}
          calls={calls}
          callTypes={callTypes}
        />
      ))}
    </React.Fragment>
  ) : (
    <div className="loading">
      <h4 style={{ opacity: 0.15 }}>No Data</h4>
    </div>
  );
};

TimeSummaryPageList.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool
};

const TimeSummaryPage = ({ actions, exported, exporting }) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD")
  });
  const [data, setData] = useState([]);
  const [refetch, setRefetch] = useState(false);
  const [pageInfo, setPageInfo] = useState({
    page: 0,
    pageSize: 1,
    pages: 1
  });

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Time-Summary-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setData([]);
    const query = queryString.stringify(
      {
        ...filters,
        paginated: true,
        page: pageInfo.page,
        pageSize: pageInfo.pageSize,
        endDate: moment(filters.endDate)
          .endOf("day")
          .format("YYYY-MM-DDTHH:mm:ss")
      },
      { arrayFormat: "bracket" }
    );
    const result = await ReportService.get(`/time-summary?${query}`);
    setData(result.data);
    setPageInfo((prev) => ({ ...prev, pages: result.pages }));
    setLoading(false);
  }, [filters, pageInfo.page, pageInfo.pageSize]);

  useEffect(() => {
    if (refetch) {
      fetchData();
      setRefetch(false);
    }
  }, [fetchData, refetch]);

  const handleExport = () => {
    const payload = {
      data: {
        ...filters,
        paginated: false,
        endDate: moment(filters.endDate)
          .endOf("day")
          .format("YYYY-MM-DDTHH:mm:ss")
      },
      type: "TIME_SUMMARY",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  const handleExportCSV = () => {
    downloadTimeSummaryCSV(
      `TimeSummary-${formatDate(moment(), "yyyyMMdd")}`,
      data,
      TIME_SUMMARY_COLUMNS,
      SUMMARY_COLUMNS,
      filters.startDate,
      filters.endDate
    );
  };

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (
      Array.isArray(value) &&
      (name === "departments" || name === "callTypes" || name === "projects")
    ) {
      setFilters((prev) => ({ ...prev, [name]: value.map((v) => v.value) }));
    } else if (name === "customers") {
      const valuesOnly = value?.map((v) => v.value);

      setFilters((prev) => ({
        ...prev,
        [name]: valuesOnly
      }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: moment().startOf("month").format("YYYY-MM-DD"),
      endDate: moment().endOf("month").format("YYYY-MM-DD")
    });
    setData([]);
  };

  return (
    <div className="report-page">
      <TimeSummaryFilters
        filters={filters}
        onChange={handleChange}
        fetchData={fetchData}
        handleResetFilters={handleResetFilters}
        exportDisabled={loading || exporting || !data?.length}
        loading={loading || exporting}
        onCSVExport={handleExportCSV}
        onPDFExport={handleExport}
      />
      <TimeSummaryPageList
        data={data}
        exporting={exporting}
        loading={loading}
        pageInfo={pageInfo}
        onExport={handleExport}
      />
    </div>
  );
};

TimeSummaryPage.propTypes = {
  actions: PropTypes.object,
  exporting: PropTypes.bool,
  exported: PropTypes.string
};

const mapStatesToProps = (state) => ({
  exported: state.reducers.exported,
  exporting: state.reducers.exporting
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...exportActions
    },
    dispatch
  )
});
export default connect(mapStatesToProps, mapDispatchToProps)(TimeSummaryPage);
